import {
  Card,
  Cell,
  Column,
  EmptyState,
  LoadingOverlay,
  PageToolbar,
  Row,
  SegmentedTabs,
  Select,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import { useRateCard } from '../../hooks/use-provider-tabs';
import { CommonUtility } from '@utility/common';
import { useMemo, useState } from 'react';
import {
  ALL_TYPES,
  billedCategories,
  filterMaterials,
  materialTypeOptions,
  type PackagingMaterial,
  type RateCardData,
  type RateCategory,
} from '../../lib/rate-card';

const VIEWS = [
  { id: 'billing', label: 'Billing' },
  { id: 'materials', label: 'Packaging materials' },
];

const MATERIAL_COLUMNS = 5;

function CategoryCard({ category }: { category: RateCategory }) {
  return (
    <Card title={category.name} bodyClassName="sk:p-0">
      <ul className="sk:m-0 sk:list-none sk:divide-y sk:divide-rule sk:p-0">
        {category.services.map(service => (
          <li
            key={service.uniqueName || service.name}
            className="sk:flex sk:items-baseline sk:justify-between sk:gap-4 sk:px-4 sk:py-2.5 sk:text-dense"
          >
            <span className="sk:min-w-0">
              {service.showTitle !== false ? (
                <span className="sk:font-medium">{service.name}</span>
              ) : null}
              {service.frequency ? (
                <span className="sk:ml-1 sk:text-fg-tertiary">
                  ({service.frequency})
                </span>
              ) : null}
            </span>
            <span className="sk:whitespace-nowrap sk:tabular-nums">
              <span className="sk:font-medium">
                {CommonUtility.currencyFormat(service.rate, 'USD')}
              </span>
              {service.billingUnit ? (
                <span className="sk:text-fg-tertiary">
                  {' '}
                  / {service.billingUnit}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Billing({ data, loading }: { data: RateCardData; loading: boolean }) {
  const categories = useMemo(() => billedCategories(data), [data]);

  if (loading) {
    return (
      <div className="sk:relative sk:min-h-60">
        <LoadingOverlay label="Loading rates" />
      </div>
    );
  }
  if (!categories.length) {
    return (
      <Card>
        <EmptyState
          title="No rates yet"
          description="The provider has not enabled any billed services."
        />
      </Card>
    );
  }
  return (
    <div className="sk:grid sk:grid-cols-3 sk:items-start sk:gap-4">
      {categories.map(category => (
        <CategoryCard key={category.name} category={category} />
      ))}
    </div>
  );
}

function Dimensions({ material }: { material: PackagingMaterial }) {
  const round = CommonUtility.roundNumber;
  return (
    <span className="sk:tabular-nums">
      {round(material.length)} × {round(material.width)} ×{' '}
      {round(material.height)}
    </span>
  );
}

function Materials({
  materials,
  loading,
}: {
  materials: PackagingMaterial[];
  loading: boolean;
}) {
  return (
    <TableFrame
      aria-label="Packaging materials"
      columnCount={MATERIAL_COLUMNS}
      isLoading={loading}
      emptyMessage="No packaging materials"
      header={
        <TableHeader>
          <Column id="name" isRowHeader>
            Name
          </Column>
          <Column id="type" className="sk:w-48">
            Type
          </Column>
          <Column id="cbf" className="sk:w-32">
            CBF
          </Column>
          <Column id="price" className="sk:w-32">
            Price
          </Column>
          <Column id="dimensions" className="sk:w-48">
            Dimensions (in.)
          </Column>
        </TableHeader>
      }
    >
      {materials.map((material, index) => (
        <Row
          key={material._id ?? `${material.name}-${index}`}
          id={material._id ?? `${material.name}-${index}`}
        >
          <Cell>{material.name}</Cell>
          <Cell>{material.packagingType}</Cell>
          <Cell className="sk:tabular-nums">
            {CommonUtility.roundNumber(material.cbf)}
          </Cell>
          <Cell className="sk:tabular-nums">
            {material.price?.value
              ? CommonUtility.currencyFormat(
                  material.price.value,
                  material.price.currency,
                )
              : 'Free'}
          </Cell>
          <Cell>
            <Dimensions material={material} />
          </Cell>
        </Row>
      ))}
    </TableFrame>
  );
}

/** What the WMS provider charges: billed services and packaging materials. */
export function RateCardTab({ providerId }: { providerId: string }) {
  const {
    rates: data,
    ratesLoading: loading,
    materials: materialData,
    materialsLoading: materialLoading,
  } = useRateCard(providerId);
  const [view, setView] = useState(VIEWS[0].id);
  const [type, setType] = useState(ALL_TYPES);

  const materials = (materialData ?? []) as PackagingMaterial[];
  const typeOptions = useMemo(
    () => materialTypeOptions(materials),
    [materials],
  );

  return (
    <div className="sk:flex sk:flex-col">
      <PageToolbar
        className="sk:mx-0"
        leading={
          <SegmentedTabs
            label="Rate card view"
            items={VIEWS}
            selectedKey={view}
            onSelectionChange={setView}
          />
        }
      >
        {view === 'materials' ? (
          <Select
            aria-label="Packaging type"
            options={typeOptions}
            value={type}
            onChange={setType}
            className="sk:w-48"
          />
        ) : null}
      </PageToolbar>
      {view === 'billing' ? (
        <Billing data={data as RateCardData} loading={loading} />
      ) : (
        <Materials
          materials={filterMaterials(materials, type)}
          loading={materialLoading}
        />
      )}
    </div>
  );
}
